'use client';
import { cn } from '@bazaar-marketplace/utils';
import { disableVerticalSwipes, init, swipeBehavior, viewport } from '@telegram-apps/sdk-react';
import { useEffect, type ReactNode } from 'react';

import { isTelegramApp } from '~/lib/telegram';

import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { usePathname } from 'next/navigation';
import { PageLogo } from '~/components/page-logo';
import { ROUTES } from '~/constants';
import { env } from '~/env/client';
import { useTelegramBackButton } from '~/hooks/use-telegram-back-button';
import { isMobileDevice } from '~/lib/utils';

export const PageWrapper = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();

  // Handle Telegram back button
  useTelegramBackButton();

  useEffect(() => {
    async function initApp() {
      init();
      swipeBehavior.mount();

      if (!viewport.isMounting()) {
        await viewport.mount();
      }
      viewport.expand();
      swipeBehavior.disableVertical();

      if (viewport.requestFullscreen.isAvailable() && isMobileDevice()) {
        await viewport.requestFullscreen();
        disableVerticalSwipes();
      }

      disableVerticalSwipes();
    }

    if (isTelegramApp(window)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      initApp();
    }
  }, []);

  // disable padding on pages that show edge-to-edge horizontal content
  const isPaddingHidden =
    pathname === '/' ||
    pathname.startsWith(ROUTES.COLLECTION_DETAIL('')) ||
    pathname.startsWith(ROUTES.JOIN_TO_GET_GOODIES('')) ||
    pathname.startsWith(ROUTES.WORKSHOP) ||
    pathname.startsWith(ROUTES.WORKSHOP_BURNING) ||
    pathname.startsWith(ROUTES.WORKSHOP_BURNING_DETAIL(''));
  pathname.startsWith(ROUTES.WORKSHOP_LEADERBOARD_EVENT(''));

  const hideLogo =
    pathname.includes(ROUTES.COLLECTIONS + '/') ||
    pathname.startsWith(ROUTES.WORKSHOP_BURNING) ||
    pathname.startsWith(ROUTES.WORKSHOP_LEADERBOARD_EVENT('')) ||
    pathname.startsWith(ROUTES.WORKSHOP_STAKING) ||
    pathname.startsWith(ROUTES.WORKSHOP_FORGE) ||
    pathname.startsWith(ROUTES.WORKSHOP_LEADERBOARD);

  return (
    <TonConnectUIProvider manifestUrl={env.NEXT_PUBLIC_TON_CONNECT_MANIFEST_URL}>
      <PageLogo hidden={hideLogo} />

      <div className={cn('w-full min-h-[100vh] px-4 pt-[110px] grid', isPaddingHidden && 'p-0')}>{children}</div>
    </TonConnectUIProvider>
  );
};
